/**
 * @typedef {import('@/types/structured-data-editor').FieldConfidence} FieldConfidence
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

const LOW_CONFIDENCE_SCORE = 60;

const TRUTHY_STRINGS = new Set(["true", "1", "yes", "oui", "vrai"]);
const FALSY_STRINGS = new Set(["false", "0", "no", "non", "faux"]);

/**
 * @param {unknown} value
 * @returns {boolean|null}
 */
export function coerceToBoolean(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "boolean") {
        return value;
    }
    const normalized = String(value).trim().toLowerCase();
    if (TRUTHY_STRINGS.has(normalized)) {
        return true;
    }
    if (FALSY_STRINGS.has(normalized)) {
        return false;
    }
    return null;
}

/**
 * @param {(key: string) => string} translate
 * @returns {Array<{ text: string, value: boolean }>}
 */
export function getBooleanSelectItems(translate) {
    return [
        { text: translate("sde.boolean.yes"), value: true },
        { text: translate("sde.boolean.no"), value: false },
    ];
}

/**
 * @param {unknown} value
 * @param {(key: string) => string} translate
 * @returns {string}
 */
export function formatBooleanDisplayValue(value, translate) {
    const bool = coerceToBoolean(value);
    if (bool === null) {
        return "—";
    }
    return translate(bool ? "sde.boolean.yes" : "sde.boolean.no");
}

/**
 * @param {FieldConfidence|undefined} confidence
 * @param {boolean} [edited]
 * @returns {string[]}
 */
export function cellClasses(confidence, edited = false) {
    if (edited || confidence?.level === "manual") {
        return ["sde-cell--edited"];
    }
    if (confidence?.level === "csv" || confidence?.level === "high") {
        return [];
    }
    if (confidence?.level === "medium") {
        return ["sde-cell--confidence-medium"];
    }
    if (confidence?.level === "low") {
        return ["sde-cell--confidence-low"];
    }
    const score = confidence?.score;
    if (score != null && score < LOW_CONFIDENCE_SCORE) {
        return ["sde-cell--confidence-low"];
    }
    if (score != null && score < 85) {
        return ["sde-cell--confidence-medium"];
    }
    return [];
}

/**
 * @param {unknown} value
 * @param {string} [fieldType]
 * @returns {string}
 */
export function formatDisplayValue(value, fieldType) {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    if (fieldType === "boolean") {
        const bool = coerceToBoolean(value);
        if (bool === null) {
            return String(value);
        }
        return bool ? "Yes" : "No";
    }
    return String(value);
}

/**
 * @param {FieldConfidence|undefined} confidence
 * @returns {boolean}
 */
export function isLowConfidence(confidence) {
    if (!confidence) {
        return false;
    }
    if (confidence.level === "low") {
        return true;
    }
    if (confidence.level === "csv" || confidence.level === "high" || confidence.level === "manual") {
        return false;
    }
    return confidence.score != null && confidence.score < LOW_CONFIDENCE_SCORE;
}

/**
 * @param {SdeRow} row
 * @returns {boolean}
 */
export function rowHasLowConfidenceField(row) {
    return Object.values(row.fields || {}).some((field) => {
        if (!field || field.deleted || field.hidden) {
            return false;
        }
        return isLowConfidence(field.confidence);
    });
}

/**
 * @param {SdeRow} row
 * @returns {boolean}
 */
export function rowHasManualField(row) {
    return Object.values(row.fields || {}).some(
        (field) => field?.confidence?.level === "manual"
    );
}
